import {
    StaticDashboard,
    StaticDashboardData
} from "./model";

import { AuthIDTokenSupplierFactory } from "../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../logging/ClientLogger";
import ClientLoggerFactory from "../logging/ClientLoggerFactory";
import { CreateStaticDashboardDataInput } from "../../API";
import { DashboardService } from "./DashboardService";
import GraphQLAPI from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../auth/IDTokenSupplier";
import { Storage } from "@aws-amplify/storage";
import { createStaticDashboardData } from "../../graphql/mutations";

export class StaticDashboardService implements DashboardService {
    private static readonly ATTEMPT_CREATE_METRIC_NAME = "GraphQLDashboardDAO.create.Attempt";
    private static readonly FAILURE_CREATE_METRIC_NAME = "GraphQLDashboardDAO.create.Failure";
    private static readonly ATTEMPT_GET_METRIC_NAME = "GraphQLDashboardDAO.get.Attempt";
    private static readonly FAILURE_GET_METRIC_NAME = "GraphQLDashboardDAO.get.Failure";

    constructor(
        private readonly logger: ClientLogger,
        private readonly idTokenSupplier: IDTokenSupplier
    ) { }

    async create(propertyId: string): Promise<void> {
        try {
            this.logger.info(
                `Creating static dashboard with property id=${propertyId}`,
                undefined,
                [StaticDashboardService.ATTEMPT_CREATE_METRIC_NAME]
            );

            const authToken: string = await this.idTokenSupplier.get();
            const input: CreateStaticDashboardDataInput = {
                propertyId: propertyId
            };

            await GraphQLAPI.graphql({
                query: createStaticDashboardData,
                variables: { input },
                authToken
            });

        } catch (error) {
            this.logger.error('Error creating dashboard', error, [StaticDashboardService.FAILURE_CREATE_METRIC_NAME]);
            throw error;
        }
    }
    async get(propertyId: string): Promise<StaticDashboard> {
        try {
            this.logger.info(
                `Getting static dashboard data with property id=${propertyId}`,
                undefined,
                [StaticDashboardService.ATTEMPT_GET_METRIC_NAME]
            );
            const dashboardPath = `dashboard/${propertyId}/latest.json`;
            // Try to get the file with download: true to access its contents
            const response: any = await Storage.get(dashboardPath, {
                level: 'public',
                download: true,
                cacheControl: 'no-cache'
            });

            const serializedContent: string = await response.Body.text();
            const data = JSON.parse(serializedContent) satisfies StaticDashboardData;
            const migratedData = this.migrateData(data);

            let lastModified = 'Unknown date';

            if (data.updatedTimestamp) {
                lastModified = new Date(data.updatedTimestamp).toLocaleString();
            }

            return {
                metadata: {
                    isGenerated: true,
                    lastModified: lastModified,
                    url: `public/dashboard/property/${propertyId}`
                },
                data: migratedData
            };
        } catch (error) {
            // Only log errors if error is not due to NoSuchKey
            if (!(error instanceof Error && error.message.includes('NoSuchKey'))) {
                this.logger.error(
                    'Error fetching static dashboard data',
                    error,
                    [StaticDashboardService.FAILURE_GET_METRIC_NAME]
                );
            }

            return {
                metadata: {
                    isGenerated: false,
                },
            };
        }
    }

    // Migrate data to new format
    private migrateData(data: StaticDashboardData): StaticDashboardData {
        return {
            ...data,
            images: data.images.map(image => {
                // Old format had a single url field
                if (!image.url) {
                    return image;
                }
                return {
                    ...image,
                    urls: {
                        original: image.url
                    }
                };
            })
        };
    }
}

export const staticDashboardService: StaticDashboardService = new StaticDashboardService(
    ClientLoggerFactory.getClientLogger("GraphQLDashboardDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);